// KOMPA 2.0 — Pause Panel (Fase Blindaje · Fase 3)
//
// Two buttons rendered in the asesor Topbar:
//   * Pausa Activa  — 10 min/day, accumulable. Asesor can pause/resume.
//   * Comida        — 30 min/day, single-use, uninterrupted.
//
// When either budget is exhausted while the user is still on pause, the
// frontend triggers an idle-logout with reason='pause_exceeded' /
// 'lunch_overrun' which flips user_status → INACTIVO server-side.
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../stores/authStore';
import { Coffee, Pause, Play, Lock } from 'lucide-react';

const fmt = (s) => {
  if (s == null || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

export default function PausePanel() {
  const userRole = useAuthStore(s => s.user?.role);
  const idleLogout = useAuthStore(s => s.idleLogout);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const tickRef = useRef(null);
  // Live "ticking" elapsed seconds for the OPEN pause; merged with server data on each poll.
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/me/pause-status');
      setData(data);
      setError('');
    } catch (err) {
      console.debug('[PausePanel] poll failed:', err?.response?.status);
    }
  }, []);

  // Mount: load + poll every 15s.
  useEffect(() => {
    if (userRole !== 'asesor') return;
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [userRole, refresh]);

  // 1-Hz tick — purely visual countdown for the open pause. We do NOT call
  // the API every second; the server poll every 15s is the source of truth.
  useEffect(() => {
    if (!data?.open_pause) {
      setTick(0);
      return;
    }
    setTick(0);
    tickRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [data?.open_pause?.id]);

  // Trigger auto-logout when remaining hits 0 while still on pause.
  useEffect(() => {
    if (!data?.open_pause) return;
    const open = data.open_pause;
    const used = (open.pause_type === 'active' ? data.active_used_seconds : data.lunch_used_seconds) + tick;
    const budget = open.pause_type === 'active' ? data.active_budget_seconds : data.lunch_budget_seconds;
    if (used >= budget + 2) {
      // Past the budget by ≥2s while still open → exceeded.
      const reason = open.pause_type === 'active' ? 'pause_exceeded' : 'lunch_overrun';
      idleLogout(reason).finally(() => {
        if (typeof window !== 'undefined') window.location.href = '/login';
      });
    }
  }, [tick, data, idleLogout]);

  if (userRole !== 'asesor' || !data) return null;

  const openType = data.open_pause?.pause_type || null;
  const liveActive = (data.active_used_seconds || 0) + (openType === 'active' ? tick : 0);
  const liveLunch  = (data.lunch_used_seconds  || 0) + (openType === 'lunch'  ? tick : 0);
  const activeRemaining = Math.max(0, (data.active_budget_seconds || 0) - liveActive);
  const lunchRemaining  = Math.max(0, (data.lunch_budget_seconds  || 0) - liveLunch);
  const activeBlocked = activeRemaining <= 0 && openType !== 'active';
  const lunchBlocked  = (data.lunch_used_count || 0) >= 1 && openType !== 'lunch';

  const action = async (path) => {
    setBusy(true); setError('');
    try {
      const { data: out } = await api.post(path, {});
      setData(out.summary || out);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo procesar la acción');
    } finally {
      setBusy(false);
    }
  };

  const onActiveToggle = () => openType === 'active' ? action('/me/pause/end') : action('/me/pause/start');
  const onLunchToggle  = () => openType === 'lunch'  ? action('/me/lunch/end') : action('/me/lunch/start');

  return (
    <div data-testid="pause-panel" className="pause-panel">
      <button
        data-testid="pause-active-btn"
        className={`pause-btn ${openType === 'active' ? 'pause-btn-running' : ''} ${activeBlocked ? 'pause-btn-locked' : ''}`}
        onClick={onActiveToggle}
        disabled={busy || activeBlocked}
        title={activeBlocked ? 'Tiempo de Pausa Activa agotado' : (openType === 'active' ? 'Reanudar trabajo' : 'Iniciar Pausa Activa')}
      >
        {activeBlocked ? <Lock size={13} /> : (openType === 'active' ? <Play size={13} /> : <Pause size={13} />)}
        <span className="pause-btn-label">Pausa Activa</span>
        <span className="pause-btn-time" data-testid="pause-active-remaining">{fmt(activeRemaining)}</span>
      </button>

      <button
        data-testid="lunch-btn"
        className={`pause-btn ${openType === 'lunch' ? 'pause-btn-running' : ''} ${lunchBlocked ? 'pause-btn-locked' : ''}`}
        onClick={onLunchToggle}
        disabled={busy || lunchBlocked}
        title={lunchBlocked ? 'Comida ya consumida hoy (único uso)' : (openType === 'lunch' ? 'Volver de comida' : 'Iniciar Comida')}
      >
        {lunchBlocked ? <Lock size={13} /> : <Coffee size={13} />}
        <span className="pause-btn-label">Comida</span>
        <span className="pause-btn-time" data-testid="pause-lunch-remaining">{fmt(lunchRemaining)}</span>
      </button>

      {error && <span className="pause-panel-error" data-testid="pause-panel-error">{error}</span>}
    </div>
  );
}
